import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { QuickAddSupplier, QuickAddQuality, QuickAddSubQuality } from '../components/ui/quick-add';

const UNIT_OPTIONS = ['Meters', 'Bales', 'Thans', 'Kg', 'Pieces'];

const CreateGreyOrder = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [allSubqualities, setAllSubqualities] = useState([]);
  const [items, setItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showAddQuality, setShowAddQuality] = useState(false);
  const [showAddSubQuality, setShowAddSubQuality] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  
  const [formData, setFormData] = useState({
    order_date: new Date().toISOString().split('T')[0],
    supplier_id: '',
    city: '',
    item_id: '',
    item: '',
    quality_id: '',
    subquality_id: '',
    rate: '',
    order_unit: '',
    ordered_quantity: '',
    remarks: ''
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [suppliersRes, qualitiesRes, subqualitiesRes, itemsRes] = await Promise.all([
        api.get('/suppliers'),
        api.get('/qualities'),
        api.get('/subqualities'),
        api.get('/items')
      ]);
      setSuppliers(suppliersRes.data);
      setQualities(qualitiesRes.data);
      setAllSubqualities(subqualitiesRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to load master data');
    }
  };

  const handleQualityChange = async (qualityId) => {
    setFormData({ ...formData, quality_id: qualityId, subquality_id: '', order_unit: '' });
    
    // Filter subqualities for selected quality (multi-parent aware)
    const filtered = allSubqualities.filter(sq => (sq.quality_ids?.length ? sq.quality_ids : [sq.quality_id]).includes(qualityId));
    setSubqualities(filtered);
    
    // Auto-populate unit from quality
    try {
      const qualityRes = await api.get(`/qualities/${qualityId}`);
      const quality = qualityRes.data;
      if (quality.default_unit) {
        setFormData(prev => ({ ...prev, quality_id: qualityId, order_unit: quality.default_unit }));
        toast.success(`Unit auto-populated: ${quality.default_unit}`);
      }
    } catch {
      toast.error('Could not auto-populate unit. Please select manually.');
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.supplier_id === supplierId);
    setFormData({
      ...formData,
      supplier_id: supplierId,
      city: supplier?.city || ''
    });
  };

  const handleItemChange = (itemId) => {
    const item = items.find(i => i.item_id === itemId);
    setFormData({
      ...formData,
      item_id: itemId,
      item: item?.item_name || ''
    });
  };

  const handleAddNewItem = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    try {
      const response = await api.post('/items', { item_name: newItemName.trim() });
      toast.success('Item added successfully!');
      setItems([...items, response.data]);
      setFormData({
        ...formData,
        item_id: response.data.item_id,
        item: response.data.item_name
      });
      setNewItemName('');
      setShowAddItem(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const qty = parseFloat(formData.ordered_quantity);
    const rate = parseFloat(formData.rate);
    if (!formData.item_id) { toast.error('Item is required'); return; }
    if (!qty || qty <= 0) { toast.error('Ordered quantity must be greater than 0'); return; }
    if (!rate || rate <= 0) { toast.error('Rate must be greater than 0'); return; }
    
    setLoading(true);

    try {
      const payload = {
        ...formData,
        rate: rate,
        ordered_quantity: qty
      };

      const response = await api.post('/grey-orders', payload);
      toast.success(`Order ${response.data.order_number} created successfully!`);
      navigate(`/grey-orders/${response.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
      setLoading(false);
    }
  };

  return (
    <>
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/grey-orders')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Create Grey Order
        </h1>
        <p className="text-slate-600 mt-1">Add a new grey fabric order</p>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Order Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Order Date (alone) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order_date">Order Date *</Label>
                <Input
                  id="order_date"
                  type="date"
                  data-testid="order-date-input"
                  value={formData.order_date}
                  onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <div></div>
            </div>

            {/* Supplier and City */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="supplier" className="flex items-center justify-between">
                  <span>Supplier *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700" onClick={() => setShowAddSupplier(true)}>
                    <Plus className="h-3 w-3 mr-1" />Add New
                  </Button>
                </Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={handleSupplierChange}
                  required
                >
                  <SelectTrigger data-testid="supplier-select" className="h-11">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                        {supplier.name} - {supplier.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  type="text"
                  data-testid="city-input"
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
            </div>

            {/* Item */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="item" className="flex items-center justify-between">
                  <span>Item *</span>
                  <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
                    <DialogTrigger asChild>
                      <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700">
                        <Plus className="h-3 w-3 mr-1" />Add New
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Item</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label>Item Name *</Label>
                          <Input
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            placeholder="e.g., POPLIN, CAMBRIC"
                          />
                        </div>
                        <Button onClick={handleAddNewItem} className="w-full bg-slate-900 hover:bg-slate-800">
                          Add Item
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </Label>
                <Select
                  value={formData.item_id}
                  onValueChange={handleItemChange}
                >
                  <SelectTrigger data-testid="item-select" className="h-11">
                    <SelectValue placeholder="Select item" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.item_id} value={item.item_id}>
                        {item.item_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div></div>
            </div>

            {/* Quality and Finish Quality */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quality" className="flex items-center justify-between">
                  <span>Quality *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700" onClick={() => setShowAddQuality(true)}>
                    <Plus className="h-3 w-3 mr-1" />Add New
                  </Button>
                </Label>
                <Select
                  value={formData.quality_id}
                  onValueChange={handleQualityChange}
                  required
                >
                  <SelectTrigger data-testid="quality-select" className="h-11">
                    <SelectValue placeholder="Select quality" />
                  </SelectTrigger>
                  <SelectContent>
                    {qualities.map((quality) => (
                      <SelectItem key={quality.quality_id} value={quality.quality_id}>
                        {quality.quality_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="subquality" className="flex items-center justify-between">
                  <span>Finish Quality *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700" onClick={() => setShowAddSubQuality(true)} disabled={!formData.quality_id}>
                    <Plus className="h-3 w-3 mr-1" />Add New
                  </Button>
                </Label>
                <Select
                  value={formData.subquality_id}
                  onValueChange={(value) => setFormData({ ...formData, subquality_id: value })}
                  required
                  disabled={!formData.quality_id}
                >
                  <SelectTrigger data-testid="finish-quality-select" className="h-11">
                    <SelectValue placeholder="Select finish quality" />
                  </SelectTrigger>
                  <SelectContent>
                    {subqualities.map((subquality) => (
                      <SelectItem key={subquality.subquality_id} value={subquality.subquality_id}>
                        {subquality.subquality_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Unit and Quantity */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="order_unit">Unit *</Label>
                <Select
                  value={formData.order_unit}
                  onValueChange={(value) => setFormData({ ...formData, order_unit: value })}
                  required
                >
                  <SelectTrigger data-testid="unit-select" className="h-11">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.order_unit && (
                  <p className="text-xs text-teal-600 mt-1">Auto-populated from quality</p>
                )}
              </div>

              <div>
                <Label htmlFor="ordered_quantity">Ordered Quantity *</Label>
                <Input
                  id="ordered_quantity"
                  type="number"
                  step="0.01"
                  data-testid="quantity-input"
                  placeholder="5000"
                  value={formData.ordered_quantity}
                  onChange={(e) => setFormData({ ...formData, ordered_quantity: e.target.value })}
                  required
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="rate">Rate *</Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  data-testid="rate-input"
                  placeholder="85.50"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  required
                  className="h-11"
                />
                <p className="text-xs text-slate-500 mt-1">Rate per meter</p>
              </div>
            </div>

            {/* Remarks */}
            <div>
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea
                id="remarks"
                data-testid="remarks-input"
                placeholder="Additional notes about this order..."
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
              />
            </div>

            {/* Summary */}
            {formData.ordered_quantity && formData.rate && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">Order Value</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  ₹ {(() => {
                    const qty = parseFloat(formData.ordered_quantity || 0);
                    const rate = parseFloat(formData.rate || 0);
                    const unit = formData.order_unit;
                    // 1 Bale = 7 Thans, 1 Than = 140 Meters, so 1 Bale = 980 Meters
                    const conversionFactors = { 'Meters': 1, 'Thans': 140, 'Bales': 980 };
                    const factor = conversionFactors[unit] || 1;
                    return (qty * factor * rate).toLocaleString('en-IN', { minimumFractionDigits: 2 });
                  })()}
                </p>
                {formData.order_unit && formData.order_unit !== 'Meters' && ['Thans', 'Bales'].includes(formData.order_unit) && (
                  <>
                    <p className="text-sm text-amber-600 mt-1 font-medium">≈ Approximately</p>
                    <p className="text-xs text-slate-500 mt-1">
                      ({(() => {
                        const qty = parseFloat(formData.ordered_quantity || 0);
                        // 1 Bale = 7 Thans = 980 Meters, 1 Than = 140 Meters
                        const conversionFactors = { 'Thans': 140, 'Bales': 980 };
                        return (qty * (conversionFactors[formData.order_unit] || 1)).toLocaleString('en-IN');
                      })()} meters @ ₹{formData.rate}/meter)
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/grey-orders')}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-order-btn"
                type="submit"
                disabled={loading}
                className="flex-1 bg-slate-900 hover:bg-slate-800"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Order'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>

      <QuickAddSupplier
        open={showAddSupplier}
        onOpenChange={setShowAddSupplier}
        onCreated={(newSupplier) => {
          setSuppliers(prev => [...prev, newSupplier]);
          handleSupplierChange(newSupplier.supplier_id);
        }}
      />
      <QuickAddQuality
        open={showAddQuality}
        onOpenChange={setShowAddQuality}
        onCreated={(newQuality) => {
          setQualities(prev => [...prev, newQuality]);
          handleQualityChange(newQuality.quality_id);
        }}
      />
      <QuickAddSubQuality
        open={showAddSubQuality}
        onOpenChange={setShowAddSubQuality}
        qualities={qualities}
        defaultQualityId={formData.quality_id}
        onCreated={(newSQ) => {
          setAllSubqualities(prev => [...prev, newSQ]);
          const parents = newSQ.quality_ids?.length ? newSQ.quality_ids : [newSQ.quality_id];
          if (parents.includes(formData.quality_id)) {
            setSubqualities(prev => [...prev, newSQ]);
          }
          setFormData(prev => ({ ...prev, subquality_id: newSQ.subquality_id }));
        }}
      />
    </>
  );
};

export default CreateGreyOrder;
